import _ from 'lodash';
import { type FC, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/app/hooks/auth-provider';
import { Description, FieldGroup, Fieldset, Label } from '@/primitives/fieldset.tsx';
import { Switch } from '@/primitives/switch.tsx';
import { useFetchStaffMemberCategoriesQuery } from '@/slices/staff-member-category-api-slice.ts';
import { useFetchStaffMemberRolesQuery } from '@/slices/staff-role-api-slices.ts';
import { useSecurityContext } from '../../hooks/security-provider.tsx';
import type { AccountListModel, StaffMemberCategory } from '../../types/account.type.ts';
import {
  keywordDefinition,
  type SearchCriteriaDisplayDefinition,
} from '../../types/search-form.type.ts';
import type { StaffRoleModel } from '../../types/staff-member.type.ts';
import type { StaffMemberSearchParams } from '../../types/staff-member-search-param.type.ts';
import { UserTypeEnum } from '../../types/user-type.ts';
import SearchForm from '../search-form';
import StaffMemberCategorySelector from '../staff-member-category-selector.tsx';
import { StaffRoleSelect } from '../staff-role-select.tsx';

const StaffSearchFilterForm: FC = () => {
  const { user } = useAuth();
  const { currentAccount } = useSecurityContext();
  const { control } = useFormContext<StaffMemberSearchParams>();

  return (
    <Fieldset>
      <FieldGroup>
        <div className='rounded-xl border border-default-200 bg-default-50 px-4 py-3'>
          <div className='flex items-start gap-3'>
            <Controller
              control={control}
              name='showInactive'
              render={({ field: { onChange, onBlur, value } }) => (
                <Switch
                  id='staff-filter-show-inactive'
                  color='blue'
                  className='mt-0.5 shrink-0'
                  onChange={onChange}
                  checked={value}
                  onBlur={onBlur}
                />
              )}
            />
            <div className='min-w-0'>
              <Label
                htmlFor='staff-filter-show-inactive'
                className='cursor-pointer font-semibold text-default-800'
              >
                Include inactive staff
              </Label>
              <Description className='mt-0.5 text-default-500'>
                Show deactivated users in the staff list
              </Description>
            </div>
          </div>
        </div>
        {user?.userType !== UserTypeEnum.DrugRep && (
          <StaffRoleSelect name='role' allowAllRoles={true} />
        )}
        <StaffMemberCategorySelector accountId={currentAccount?.id} name='category' />
      </FieldGroup>
    </Fieldset>
  );
};

const StaffSearchForm: FC = () => {
  const { currentAccount } = useSecurityContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const getSearchModel = (value: URLSearchParams) => {
    // biome-ignore lint/correctness/useHookAtTopLevel: fix me
    const { data: categories } = useFetchStaffMemberCategoriesQuery();
    // biome-ignore lint/correctness/useHookAtTopLevel: fix me
    const { data: roles } = useFetchStaffMemberRolesQuery();

    const category = categories?.find(x => x.id === parseInt(value.get('category')));
    const role = roles?.find(x => x.id == parseInt(value.get('role')));

    return {
      keyword: value.get('keyword'),
      account: currentAccount,
      category: value.has('category') ? category : null,
      role: value.has('role') ? role : null,
      showInactive: value.get('showInactive') === 'true',
    } as StaffMemberSearchParams;
  };

  useEffect(() => {
    const searchAccountKey = searchParams.get('accountKey');
    const currentAccountKey = currentAccount?.key ?? null;
    if (searchAccountKey === currentAccountKey) {
      return;
    }

    setSearchParams(prev => {
      const params = new URLSearchParams(prev);

      if (currentAccountKey) {
        params.set('accountKey', currentAccountKey);
      } else {
        params.delete('accountKey');
      }

      return params;
    });
  }, [currentAccount, searchParams, setSearchParams]);

  const definitions: SearchCriteriaDisplayDefinition[] = [
    keywordDefinition,
    {
      key: 'role',
      label: 'Permission',
      displayFunc: (value: StaffRoleModel) => value?.description,
    },
    {
      key: 'account',
      label: 'Account',
      displayFunc: (value: AccountListModel) => value?.accountName,
    },
    {
      key: 'category',
      label: 'Category',
      displayFunc: (value: StaffMemberCategory) => value?.description,
    },
    {
      key: 'showInactive',
      label: 'Include inactive staff',
      displayFunc: (value: boolean) => (value ? 'Yes' : null),
    },
  ];

  const getUrlSearchParams = (value: StaffMemberSearchParams) => {
    const params = new URLSearchParams();
    if (
      value?.showInactive ||
      value?.keyword ||
      value?.category?.id ||
      value?.account?.key ||
      value?.role?.id
    ) {
      if (value.showInactive) {
        params.set('showInactive', 'true');
      }

      if (value.keyword) {
        params.set('keyword', value.keyword.trim());
      }

      if (value.role && _.isNumber(parseInt(value.role.id.toString()))) {
        params.set('role', value.role.id.toString());
      }

      if (value.account?.key) {
        params.set('accountKey', value.account.key);
      }

      if (value.category && _.isNumber(parseInt(value.category.id.toString()))) {
        params.set('category', value.category.id.toString());
      }

      return params;
    }
  };

  const defaultValue = {
    role: { id: '' },
    account: { id: '' },
    category: { id: '' },
    showInactive: false,
  };

  return (
    <SearchForm<StaffMemberSearchParams>
      defaultValue={defaultValue}
      getSearchParamModel={getSearchModel}
      getUrlSearchParams={getUrlSearchParams}
      searchCriteriaDisplayDefinitions={definitions}
      filterForm={<StaffSearchFilterForm />}
    />
  );
};

export default StaffSearchForm;
